import { useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useMachineDailyMeters } from '../../api/queries';
import type { DailyMeterRow } from '../../api/types';
import { SuspectDayDetailDialog } from '../revenue/SuspectDayDetailDialog';
import type { SuspectDayHeader } from '../revenue/SuspectDayDetailDialog';
import { fmtCurrencyCents, fmtDate, fmtPercent, fmtOrEmDash } from '../../utils/format';

/** ISO YYYY-MM-DD for `d` days ago (local). */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Daily meters — one row per gaming day for this machine, read from
 * route.machine_meter_day_delta (net machine win, turnover, hold, actual RTP
 * from the per-version deltas). These rows are the permanent financial record
 * (never purged); the 5-minute raw snapshots behind them are retained only for
 * the configured window. Grouped visibly by version so a RAM-reset boundary is
 * obvious.
 */
export function MachineDailyMetersTab({ machineId, machineLabel, gameName }: {
  machineId: string;
  machineLabel?: string;
  gameName?: string | null;
}) {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const q = useMachineDailyMeters(machineId, from, to);

  const rangeError = from > to ? 'From date must be on or before To date.' : null;
  const rows = useMemo(() => q.data ?? [], [q.data]);

  // Owner request: clicking a day row opens the full 33-meter display for that
  // day next to the neighbouring days, with the suspect-review actions inline
  // (a suspect day can be confirmed good/suspect right there).
  const [detailDay, setDetailDay] = useState<SuspectDayHeader | null>(null);
  function openDetail(r: DailyMeterRow) {
    setDetailDay({
      id: r.id,
      machineLabel: machineLabel ?? '',
      gameName: gameName ?? null,
      gamingDay: r.gamingDay,
      suspect: r.suspect,
      suspectReason: r.suspectReason,
      suspectReviewedAt: r.suspectReviewedAt,
    });
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        One row per gaming day (site-local, offset applied). Net machine win = turnover − payouts − jackpots,
        computed from per-version meter deltas. Baseline days (a version's first day) show no delta.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label="From" type="date" size="small" value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!rangeError} inputProps={{ 'aria-label': 'from date' }}
        />
        <TextField
          label="To" type="date" size="small" value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!rangeError} inputProps={{ 'aria-label': 'to date' }}
        />
        {q.isFetching && <CircularProgress size={18} />}
      </Stack>

      {rangeError && <Alert severity="warning">{rangeError}</Alert>}
      {q.isError && !rangeError && (
        <Alert severity="error">
          {(q.error as { message?: string })?.message ?? 'Failed to load daily meters.'}
        </Alert>
      )}

      {!rangeError && !q.isError && rows.length === 0 && !q.isLoading && (
        <Typography variant="body2" color="text.secondary">No daily meter rows in this range.</Typography>
      )}

      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="daily meters" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Gaming day</TableCell>
                <TableCell>Version</TableCell>
                <TableCell align="right">Turnover</TableCell>
                <TableCell align="right">Payouts</TableCell>
                <TableCell align="right">Jackpots</TableCell>
                <TableCell align="right">Net machine win</TableCell>
                <TableCell align="right">Hold</TableCell>
                <TableCell align="right">Actual RTP</TableCell>
                <TableCell>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {rows.map((r) => (
                <TableRow
                  key={`${r.gamingDay}-${r.versionNo}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetail(r)}
                >
                  <TableCell>{fmtDate(r.gamingDay)}</TableCell>
                  <TableCell>v{r.versionNo}</TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.turnoverCents)}</TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.totalOutCents)}</TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.jackpotsCents)}</TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.netWinCents)}</TableCell>
                  <TableCell align="right">{fmtPercent(r.holdPct)}</TableCell>
                  <TableCell align="right">{fmtPercent(r.actualRtpPct)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {r.isBaselineDay && <Chip size="small" label="baseline" />}
                      {r.ramResetDay && <Chip size="small" color="warning" label="RAM reset" />}
                      {r.suspect && <Chip size="small" color="error" label="suspect" />}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        {fmtOrEmDash(rows.length)} day(s) shown. Click a row to see the full meter set for that day.
      </Typography>

      {detailDay !== null && (
        <SuspectDayDetailDialog
          open
          onClose={() => setDetailDay(null)}
          day={detailDay}
          allowResolve
        />
      )}
    </Stack>
  );
}
